import { Modal } from "@mantine/core";
import { useEffect, useState } from "react";
import { PropertyToEdit } from "redux/reducers/Properties/PropertiesSliceTypes";
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { LazyLoadImage } from "react-lazy-load-image-component";
import "react-lazy-load-image-component/src/effects/blur.css";

interface ImageModalProps {
  isOpen: boolean;
  onClose: () => void;
  propertyData: PropertyToEdit;
}

const ImageModal: React.FC<ImageModalProps> = ({
  isOpen,
  onClose,
  propertyData,
}) => {
  const images = [
    ...(propertyData.cover_image ? [propertyData.cover_image] : []),
    ...(propertyData.place_images || []),
  ];
  const [fullImageUrl, setFullImageUrl] = useState<any>("");
  const [selectedIndex, setSelectedIndex] = useState<any>(null);

  useEffect(() => {
    if (selectedIndex !== null) {
      setFullImageUrl(images[selectedIndex]?.url);
    }
  }, [selectedIndex, images]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;
      
      if (e.key === "Escape") {
        modifiedClose();
      } else if (fullImageUrl) {
        if (e.key === "ArrowRight") {
          onSlideRight(selectedIndex);
        } else if (e.key === "ArrowLeft") {
          onSlideLeft(selectedIndex);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, fullImageUrl, selectedIndex]);

  const fullImageHandler = (image: any, index: number) => {
    setSelectedIndex(index);
    setFullImageUrl(image?.url);
  };

  const modifiedClose = () => {
    setFullImageUrl("");
    setSelectedIndex(null);
    onClose();
  };

  const onSlideRight = (idx: number) => {
    const updatedIndex = (idx + 1) % images.length;
    setSelectedIndex(updatedIndex);
  };

  const onSlideLeft = (idx: number) => {
    const updatedIndex = (idx - 1 + images.length) % images.length;
    setSelectedIndex(updatedIndex);
  };

  return (
    <Modal
      opened={isOpen}
      onClose={modifiedClose}
      fullScreen
      radius={0}
      transitionProps={{ transition: "fade", duration: 200 }}
      withCloseButton={false}
      styles={{
        content: {
          backgroundColor: "#000000",
        },
        body: {
          height: "100vh",
          padding: 0,
          margin: 0,
          backgroundColor: "#000000",
        },
      }}
    >
      {/* CUSTOM HEADER - Always visible */}
      <div className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-b from-black/90 via-black/50 to-transparent">
        <div className="flex items-center justify-between p-4 md:p-6">
          {/* Back/Close button - Left side on mobile, Airbnb style */}
          <button
            onClick={modifiedClose}
            className="flex items-center gap-2 text-white hover:text-gray-300 transition-colors"
            aria-label="Close gallery"
          >
            <div className="p-2 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm transition-colors">
              <XMarkIcon className="w-5 h-5 md:w-6 md:h-6" />
            </div>
            <span className="hidden md:inline text-sm font-medium">Close</span>
          </button>

          {/* Image counter - Center/Right */}
          {fullImageUrl && (
            <div className="text-white text-sm md:text-base font-medium">
              {selectedIndex + 1} / {images.length}
            </div>
          )}
        </div>
      </div>

      <div className="h-full w-full overflow-auto pt-16 md:pt-20">
        {!fullImageUrl ? (
          /* ========== GRID VIEW ========== */
          <div className="p-4 md:p-6 lg:p-8">
            <div className="max-w-7xl mx-auto">
              {/* Title */}
              <h2 className="text-white text-2xl md:text-3xl font-bold mb-6 md:mb-8">
                {propertyData.title}
              </h2>

              {images && images.length > 0 ? (
                /* Responsive Grid - Mobile: 1 column, Tablet: 2 columns, Desktop: 3 columns */
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                  {images.map((item, index) => (
                    <div
                      key={item?.id || `img-${index}`}
                      className="relative w-full cursor-pointer group overflow-hidden rounded-lg"
                      onClick={() => fullImageHandler(item, index)}
                    >
                      {/* Aspect ratio container */}
                      <div className="relative aspect-[4/3] bg-gray-800">
                        <LazyLoadImage
                          src={item?.url || ""}
                          alt={`${propertyData.title} - Image ${index + 1}`}
                          effect="blur"
                          wrapperClassName="absolute inset-0"
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                        />
                        {/* Overlay on hover */}
                        <div className="absolute inset-0 bg-black opacity-0 group-hover:opacity-20 transition-opacity duration-300" />
                        
                        {/* Image number badge */}
                        <div className="absolute top-2 left-2 bg-black/60 backdrop-blur-sm text-white text-xs font-semibold px-2 py-1 rounded">
                          {index + 1}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-white py-20">
                  <p className="text-lg">No images available</p>
                </div>
              )}
            </div>
          </div>
        ) : (
          /* ========== FULL IMAGE VIEW ========== */
          <div className="relative flex items-center justify-center w-full h-full bg-black">
            {/* Main Image Container */}
            <div className="relative w-full h-full flex items-center justify-center px-4 md:px-16">
              <LazyLoadImage
                src={fullImageUrl || ""}
                effect="blur"
                alt={`${propertyData.title} - Full view`}
                wrapperClassName="max-w-full max-h-full"
                className="object-contain max-h-[calc(100vh-8rem)]"
                style={{
                  width: "auto",
                  height: "auto",
                }}
              />
            </div>

            {/* Navigation Buttons - Desktop */}
            {images.length > 1 && (
              <>
                <button
                  className="hidden md:flex absolute left-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm p-3 text-white transition-all hover:scale-110 focus:outline-none focus:ring-2 focus:ring-white/50"
                  onClick={() => onSlideLeft(selectedIndex)}
                  aria-label="Previous image"
                >
                  <ChevronLeftIcon className="h-6 w-6" />
                </button>
                <button
                  className="hidden md:flex absolute right-4 top-1/2 -translate-y-1/2 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm p-3 text-white transition-all hover:scale-110 focus:outline-none focus:ring-2 focus:ring-white/50"
                  onClick={() => onSlideRight(selectedIndex)}
                  aria-label="Next image"
                >
                  <ChevronRightIcon className="h-6 w-6" />
                </button>
              </>
            )}

            {/* Mobile Navigation - Bottom Bar */}
            {images.length > 1 && (
              <div className="md:hidden fixed bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-4">
                <div className="flex items-center justify-between max-w-md mx-auto">
                  <button
                    className="flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm p-3 text-white transition-all active:scale-95"
                    onClick={() => onSlideLeft(selectedIndex)}
                    aria-label="Previous image"
                  >
                    <ChevronLeftIcon className="h-6 w-6" />
                  </button>

                  {/* Dot indicators for mobile */}
                  <div className="flex gap-1.5 px-4">
                    {images.slice(Math.max(0, selectedIndex - 2), Math.min(images.length, selectedIndex + 3)).map((_, idx) => {
                      const actualIndex = Math.max(0, selectedIndex - 2) + idx;
                      return (
                        <div
                          key={actualIndex}
                          className={`h-1.5 rounded-full transition-all ${
                            actualIndex === selectedIndex
                              ? "w-6 bg-white"
                              : "w-1.5 bg-white/40"
                          }`}
                        />
                      );
                    })}
                  </div>

                  <button
                    className="flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 backdrop-blur-sm p-3 text-white transition-all active:scale-95"
                    onClick={() => onSlideRight(selectedIndex)}
                    aria-label="Next image"
                  >
                    <ChevronRightIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>
            )}

            {/* Thumbnail Strip - Desktop Only */}
            <div className="hidden lg:block fixed bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-6">
              <div className="max-w-5xl mx-auto">
                <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
                  {images.map((item, index) => (
                    <button
                      key={item?.id || `thumb-${index}`}
                      onClick={() => {
                        setSelectedIndex(index);
                        setFullImageUrl(item?.url);
                      }}
                      className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden transition-all ${
                        selectedIndex === index
                          ? "ring-2 ring-white opacity-100 scale-105"
                          : "opacity-60 hover:opacity-100"
                      }`}
                    >
                      <img
                        src={item?.url}
                        alt={`Thumbnail ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default ImageModal;